/**
 * DI-02 outcome builder — turns a raw deck scan into what the chat card shows.
 *
 * Pure function (no Office.js), so every G-7 branch is unit-testable:
 *   - some notes                → "N slides with notes:" + cards (+ partial footnote)
 *   - all readable, none set    → "No speaker notes set on any slide."
 *   - nothing readable / no API → "AiVA can't read speaker notes in this version of PowerPoint."
 *   - no slides                 → "This presentation has no slides."
 */

import {
  NOTES_PREVIEW_MAX_CHARS,
  SPEAKER_NOTES_COPY,
  speakerNotesHeader,
  speakerNotesPartialFootnote,
} from "../../../const";
import type { SlideNotes, SpeakerNotesListResult, SpeakerNotesScan } from "../types";

/** Caps a notes preview at NOTES_PREVIEW_MAX_CHARS with a trailing ellipsis. */
export function truncateNotes(text: string, max: number = NOTES_PREVIEW_MAX_CHARS): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max).trimEnd()}…`;
}

export function buildSpeakerNotesResult(scan: SpeakerNotesScan | null): SpeakerNotesListResult {
  if (!scan) {
    return { outcome: "none_readable", header: SPEAKER_NOTES_COPY.NONE_READABLE, items: [] };
  }
  if (scan.totalSlides === 0) {
    return { outcome: "empty_deck", header: SPEAKER_NOTES_COPY.EMPTY_DECK, items: [] };
  }

  const items: SlideNotes[] = scan.notes.map((n) => ({ slideNumber: n.slideNumber, text: truncateNotes(n.text) }));

  if (items.length > 0) {
    return {
      outcome: "has_notes",
      header: speakerNotesHeader(items.length),
      items,
      ...(scan.unreadableCount > 0 ? { footnote: speakerNotesPartialFootnote(scan.unreadableCount) } : {}),
    };
  }

  if (scan.unreadableCount > 0 && scan.emptyCount === 0) {
    return { outcome: "none_readable", header: SPEAKER_NOTES_COPY.NONE_READABLE, items: [] };
  }

  // Some slides readable and empty. If others were unreadable we still can't
  // claim "none set" for the whole deck, so add the partial footnote.
  return {
    outcome: "none_set",
    header: SPEAKER_NOTES_COPY.NONE_SET,
    items: [],
    ...(scan.unreadableCount > 0 ? { footnote: speakerNotesPartialFootnote(scan.unreadableCount) } : {}),
  };
}

/** Convenience for the chat layer: read the deck and build the card result in one call. */
export async function listSpeakerNotes(
  read: () => Promise<SpeakerNotesScan | null>,
): Promise<SpeakerNotesListResult> {
  return buildSpeakerNotesResult(await read());
}
